/* eslint-disable react/prop-types */
/* eslint-disable react-native/no-inline-styles */
/*
Author: Gilang Romadhanu Tartila
*/

import React, { useEffect, useMemo } from 'react';
import { View } from 'react-native';
import { isWeb, fullWidth } from '@global/screens';
import Colors from '@global/colors';
import { hasNoAccess } from '@global/helpers';
import Button from '@components/form/Button';
import SociollaText from '@components/sociolla/SociollaText';
import StatusBadge from '@components/global/StatusBadge';
import ActionButton from '@components/navbar/ActionButton';
import NotificationSettingButton from '@components/navbar/NotificationSettingButton';
import Table from '@components/table/Table';
import TableMobile from '@components/table/TableMobile';
import * as Layout from '@components/screen/Layout';
import useListScreen from '@/hooks/useListScreen';
import {
  useGetNewArrivalBestSellerListQuery,
  useGetNewArrivalBestSellerCountQuery,
  useGetNewArrivalBestSellerInfiniteListInfiniteQuery,
} from '@api/listApi';

const ADD_ROLES_BLOCKLIST = [
  'sales_supervisor',
  'branch_head',
  'finance_supervisor',
  'regional_manager',
  'regional_support',
  'key_account_manager',
  'group_key_account_manager',
  'national_key_account_manager',
  'distributor_manager',
  'general_manager',
  'vice_president',
  'vice_president_finance',
  'budget_controller',
  'finance_head',
  'tax_support',
  'billing_supervisor',
  'regional_distributor_manager',
];

const tableStructure = {
  name: {
    head: 'Title',
    body: 'name',
    textStyle: { fontFamily: 'figtree-medium' },
  },
  flag_type: {
    head: 'Flag Type',
    body: 'flag_type',
    type: 'slot',
  },
  is_active: {
    head: 'Status',
    body: 'is_active',
    type: 'status_active',
  },
};

// Search box writes `name` (→ $regex).
const toApiFilter = (raw = {}) => {
  const filter = {};
  const name = raw.name?.$regex ?? raw.name;
  if (name) {
    filter.name = { $regex: name, $options: 'i' };
  }
  return filter;
};

const mapRow = (item) => ({ ...item, id: item._id });

const NewArrivalBestSellerScreen = (props) => {
  const { list, count, openChild, scope, params } = useListScreen({
    listHook: useGetNewArrivalBestSellerListQuery,
    countHook: useGetNewArrivalBestSellerCountQuery,
    infiniteHook: useGetNewArrivalBestSellerInfiniteListInfiniteQuery,
    toFilter: toApiFilter,
  });

  const showAddButton = hasNoAccess(ADD_ROLES_BLOCKLIST);

  const searchValue = useMemo(() => {
    const k = params.filter?.name;
    return k && k.$regex ? k.$regex : k ?? '';
  }, [params.filter]);

  const rows = useMemo(() => {
    const raw = isWeb
      ? Array.isArray(list.data)
        ? list.data
        : []
      : (list.data?.pages ?? []).flat();
    return raw.map(mapRow);
  }, [list.data]);

  const goToForm = () => {
    openChild('NewArrivalBestSellerFormScreen');
  };

  const goToDetail = ({ data }) => {
    openChild('NewArrivalBestSellerDetailScreen', {
      id: data._id,
      titleScreen: data.title,
    });
  };

  // Own the header so the mobile Add goes through openChild (preserves the list
  // on return) rather than the navigator's plain navigate.
  useEffect(() => {
    props.navigation.setOptions({
      headerRight: () =>
        isWeb ? (
          <NotificationSettingButton {...props} />
        ) : showAddButton ? (
          <ActionButton
            icon={require('@assets/img/icons/plus-white.svg')}
            onCallback={goToForm}
          />
        ) : (
          false
        ),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.navigation, showAddButton]);

  return (
    <Layout.Container>
      {isWeb ? (
        <>
          <Layout.Title title="Best Seller / New Arrival">
            {showAddButton ? (
              <View style={{ flexDirection: 'row' }}>
                <Button
                  type="primary"
                  rounded={true}
                  buttonStyle={{ width: 100 }}
                  title={
                    <SociollaText
                      type="medium"
                      style={{ fontSize: 14, color: Colors.white }}>
                      + Add
                    </SociollaText>
                  }
                  onPress={goToForm}
                />
              </View>
            ) : (
              false
            )}
          </Layout.Title>

          <Table
            filterName={scope}
            searchKey="name"
            searchPlaceholder="Search by title"
            structures={tableStructure}
            data={rows}
            loading={list.isLoading}
            refetching={list.isFetching}
            hasNoHide={true}
            totalData={count.data}
            skip={params.skip}
            limit={params.limit}
            hasCallback={true}
            showFilter={false}
            onClickRow={(val) => goToDetail(val)}
            searchValue={searchValue}
            slot={{
              flag_type: ({ data, index }) => (
                <SociollaText key={index} style={{ fontSize: 12 }}>
                  {data.flag_type == 'best_seller'
                    ? 'Best Seller'
                    : 'New Arrival'}
                </SociollaText>
              ),
            }}
          />
        </>
      ) : (
        <TableMobile
          filterName={scope}
          searchKey="name"
          placeholder="Search by title"
          loading={list.isLoading}
          refetching={list.isFetching}
          loadingLoadMore={list.isFetchingNextPage}
          hasNextPage={list.hasNextPage}
          onLoadMore={list.fetchNextPage}
          onRefresh={list.refetch}
          skip={params.skip}
          limit={params.limit}
          data={rows}
          totalData={count.data}
          searchValue={searchValue}
          renderItem={({ data, index }) => (
            <Layout.ItemMobile
              key={index}
              touchable={true}
              column={true}
              onPress={() => goToDetail({ data })}>
              <View
                style={{
                  flexDirection: 'row',
                  alignItems: 'flex-start',
                  justifyContent: 'space-between',
                }}>
                <View style={{ width: fullWidth - 140 }}>
                  <SociollaText
                    type="medium"
                    style={{ width: '100%', marginBottom: 4 }}>
                    {data.name}
                  </SociollaText>
                  <SociollaText style={{ width: '100%', color: Colors.grey1 }}>
                    {data.flag_type == 'best_seller'
                      ? 'Best Seller'
                      : 'New Arrival'}
                  </SociollaText>
                </View>
                <StatusBadge status={data.is_active ? 'active' : 'in_active'} />
              </View>
            </Layout.ItemMobile>
          )}
        />
      )}
    </Layout.Container>
  );
};

export default NewArrivalBestSellerScreen;
